import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, LogOut, PlusCircle, Search, Filter, X, Download, FileText, Edit2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LanguageSelector from "@/components/LanguageSelector";
import GalaxyBackground from "@/components/GalaxyBackground";
import ScriptInitial from "@/components/ScriptInitial";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAG_OPTIONS = [
  "nightmare",
  "lucid",
  "lucidDream",
  "recurring",
  "vivid",
  "flying",
  "falling",
  "water",
  "chase",
  "adventure",
  "peaceful"
];

export default function DreamsLibrary({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dreams, setDreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchDreams();
  }, [selectedTags, searchQuery]);

  const fetchDreams = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedTags.length > 0) {
        params.tags = selectedTags.join(',');
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await axios.get(`${API}/dreams`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setDreams(response.data);
    } catch (error) {
      toast.error("Failed to load dreams");
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/dreams/export/csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `uneksia-dreams-${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('dreams.exportSuccess') || 'Unet ladattu CSV-tiedostona!');
    } catch (error) {
      toast.error(t('dreams.exportError') || 'CSV-lataus epäonnistui');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen relative">
      <GalaxyBackground />
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
                <span className="text-2xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
              </div>
              <div className="hidden md:flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dreams')}
                  className="font-body text-lucid hover:text-lucid/80 font-medium"
                  data-testid="nav-dreams"
                >
                  {t('nav.myDreams')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/community-hub')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-community-hub"
                >
                  {t('nav.community')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/blog")}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-blog"
                >
                  {t("blog.title")}
                </Button>
                {user?.is_admin && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/admin')}
                    className="font-body text-red-500 hover:text-red-600 font-medium"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Button>
                )}
                
                {!user?.is_premium && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/premium')}
                    className="font-body text-lucid hover:text-lucid/80 font-medium"
                    data-testid="nav-premium"
                  >
                    ✨ {t('nav.premium')}
                  </Button>
                )}
                
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:block">
                <LanguageSelector variant="minimal" />
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate('/blog')}
                className="sm:hidden font-body text-void hover:text-void/80 px-2 text-sm"
                data-testid="nav-blog-mobile"
              >
                {t("blog.title")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/games')}
                className="sm:hidden font-body text-void hover:text-void/80 px-2 text-sm"
                data-testid="nav-games-mobile"
              >
                {t('games.navTitle') || 'Pelit'}
              </Button>
              <Button
                onClick={() => navigate('/journal')}
                className="hidden sm:flex bg-void text-white hover:bg-void/90 rounded-full font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
                data-testid="new-dream-button"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {t('nav.newDream')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/settings')}
                className="text-void hover:text-void/80"
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-void hover:text-void/80"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-32 md:pb-12">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl sm:text-5xl font-heading font-light text-void tracking-tight mb-2" data-testid="library-title">
              <span className="font-script">{t('library.title')}</span>
            </h1>
            <p className="text-lg text-void/70 font-body tracking-wide">
              <ScriptInitial>{t('library.subtitle')}</ScriptInitial>
            </p>
          </div>
          {dreams.length > 0 && (
            <button
              onClick={handleExportCSV}
              className="p-2 text-void/40 hover:text-void transition-colors rounded-full hover:bg-white/30"
              title={t('dreams.exportCSV') || 'Lataa CSV'}
              data-testid="export-csv-button"
            >
              <Download className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Search and Filter */}
        <div className="glass-effect rounded-3xl p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-void/40" />
              <Input
                type="text"
                placeholder={t('dreams.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl h-12 pl-12"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white/50 text-void border-void/10 hover:bg-white/80 rounded-xl h-12 font-body"
              data-testid="filter-button"
            >
              <Filter className="w-4 h-4 mr-2" />
              {t('dreams.filterByTags')} {selectedTags.length > 0 && `(${selectedTags.length})`}
            </Button>
          </div>

          {/* Filter Tags */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-white/30" data-testid="filter-tags-container">
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-4 py-2 rounded-full font-body text-sm transition-all duration-300 ${
                      selectedTags.includes(tag)
                        ? tag === 'nightmare'
                          ? 'bg-red-500/80 text-white'
                          : tag === 'lucidDream'
                            ? 'bg-indigo-500/80 text-white'
                            : 'bg-lucid text-void'
                        : tag === 'nightmare'
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : tag === 'lucidDream'
                            ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                            : 'bg-white/30 text-void/70 hover:bg-white/50'
                    }`}
                    data-testid={`filter-tag-${tag}`}
                  >
                    {t(`tags.${tag}`)}
                  </button>
                ))}
                {selectedTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedTags([])}
                    className="px-4 py-2 rounded-full font-body text-sm bg-void/10 text-void hover:bg-void/20 transition-all"
                    data-testid="clear-filters-button"
                  >
                    {t('common.cancel')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Dreams Grid */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-dreams">
            <p className="text-void/60 font-body">{t('common.loading')}</p>
          </div>
        ) : dreams.length === 0 ? (
          <div className="glass-effect rounded-3xl p-12 text-center" data-testid="no-dreams">
            <p className="text-void/60 font-body mb-4">
              {t('dreams.noDreams')}
            </p>
            {!searchQuery && selectedTags.length === 0 && (
              <Button
                onClick={() => navigate('/journal')}
                className="bg-void text-white hover:bg-void/90 rounded-full font-body"
                data-testid="start-recording-button"
              >
                {t('dreams.startJourney')}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dreams-grid">
            {/* Sort: drafts first, then by date */}
            {[...dreams].sort((a, b) => {
              if (a.is_draft && !b.is_draft) return -1;
              if (!a.is_draft && b.is_draft) return 1;
              return 0;
            }).map((dream) => (
              <div
                key={dream.id}
                onClick={() => dream.is_draft ? navigate(`/journal/edit/${dream.id}`) : navigate(`/dreams/${dream.id}`)}
                className={`glass-effect rounded-3xl p-6 hover:shadow-lg transition-all duration-500 hover:-translate-y-1 cursor-pointer dream-card-hover relative ${
                  dream.is_draft ? 'border-2 border-amber-300/60' : ''
                }`}
                data-testid={`dream-card-${dream.id}`}
              >
                {/* Draft Badge */}
                {dream.is_draft && (
                  <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-body font-medium" data-testid={`draft-badge-${dream.id}`}>
                    <FileText className="w-3 h-3" />
                    {t('journal.draft')}
                  </div>
                )}
                <div className="mb-4">
                  <h3 className="text-xl font-heading font-light text-void mb-2 pr-20">{dream.title}</h3>
                  <p className="text-sm text-void/60 font-body">{formatDate(dream.date)}</p>
                </div>
                <p className="text-void/70 font-body line-clamp-3 mb-4">{dream.content}</p>
                <div className="flex items-center justify-between">
                  {dream.tags && dream.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {dream.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1 bg-lucid/20 text-void rounded-full font-body"
                        >
                          {t(`tags.${tag}`) || tag}
                        </span>
                      ))}
                      {dream.tags.length > 3 && (
                        <span className="text-xs px-3 py-1 bg-ethereal/20 text-void rounded-full font-body">
                          +{dream.tags.length - 3}
                        </span>
                      )}
                    </div>
                  ) : <div />}
                  {dream.is_draft && (
                    <span className="text-xs text-amber-600 font-body flex items-center gap-1">
                      <Edit2 className="w-3 h-3" />
                      {t('journal.continueDraft')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}